// Post-processing pipeline.
//
// The game previously ended its frame at a single forward `renderer.render(scene, camera)`, so
// every `emissive:` material in the world — gate lights, crystals, boss auras, spell orbs — just
// clipped to white instead of glowing, and there was no grading or vignette at all.
//
// Design notes that are easy to get wrong:
//
// 1. TONE MAPPING STAYS ON THE RENDERER. three's WebGLPrograms forces NoToneMapping and linear
//    output whenever `currentRenderTarget !== null`, so RenderPass already produces linear HDR
//    without touching `renderer.toneMapping`. OutputPass then reads `toneMapping`,
//    `outputColorSpace`, and `toneMappingExposure` off the renderer every frame and applies them
//    last — which is exactly what we want, and it means the existing Scene Exposure setting
//    (`renderer.toneMappingExposure`) keeps working with no changes.
//
// 2. MSAA MUST BE RE-SUPPLIED. `antialias: true` on the WebGLRenderer is silently ignored once
//    rendering targets a render target, and EffectComposer takes no `samples` option. We pass our
//    own WebGLRenderTarget with `samples`, or turning the composer on would visibly *worsen*
//    edges. `samples` is immutable after allocation, so changing quality rebuilds the composer.
//
// 3. NO DEPTH SAMPLING. EffectComposer ping-pongs between two targets and
//    `RenderTarget.copy()` gives the second its own *cloned* depth texture, so a pass that
//    samples "the" depth texture reads the wrong buffer on alternating frames. Depth-based edge
//    detection and true soft particles are therefore out; silhouette separation comes from the
//    rim light and the selection rings instead (see renderer.ts).

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export type QualityPreset = 'low' | 'medium' | 'high' | 'ultra';

/** Per-preset pipeline shape. Low is absent entirely — the renderer bypasses the composer. */
interface FxProfile {
  /** MSAA samples on the HDR target. 0 disables MSAA and enables SMAA instead. */
  samples: number;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
  /** bloom mip chain resolution as a fraction of the drawing buffer */
  bloomScale: number;
  saturation: number;
  contrast: number;
  vignette: number;
}

const PROFILES: Record<Exclude<QualityPreset, 'low'>, FxProfile> = {
  // TEST — all grading values are authored by eye against the existing 0.88 ACES exposure and
  // want an owner pass; they are the "blend" art direction (AoE4 restraint in the grade,
  // BattleForge punch in the bloom threshold).
  medium: { samples: 2, bloomStrength: 0.34, bloomRadius: 0.5, bloomThreshold: 0.86, bloomScale: 0.5, saturation: 1.06, contrast: 1.03, vignette: 0.22 },
  high: { samples: 4, bloomStrength: 0.46, bloomRadius: 0.62, bloomThreshold: 0.82, bloomScale: 0.75, saturation: 1.09, contrast: 1.05, vignette: 0.26 },
  ultra: { samples: 8, bloomStrength: 0.54, bloomRadius: 0.7, bloomThreshold: 0.78, bloomScale: 1, saturation: 1.11, contrast: 1.06, vignette: 0.28 },
};

/**
 * Filmic grade: lift/gamma/gain-ish contrast around a pivot, saturation, and a vignette.
 * Deliberately cheap — one full-screen pass, no texture lookups beyond the source.
 */
const GradeShader = {
  name: 'SigilfrontGrade',
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    saturation: { value: 1.08 },
    contrast: { value: 1.04 },
    vignette: { value: 0.26 },
    // Warm the highlights and cool the shadows slightly — the cheapest way to read as "graded"
    // rather than "raw render" without a LUT texture.
    warmth: { value: 0.035 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float contrast;
    uniform float vignette;
    uniform float warmth;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 color = texel.rgb;

      // Rec.709 luma, so saturation and contrast preserve perceived brightness.
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));

      color = mix(vec3(luma), color, saturation);
      // Contrast about a 0.18 middle-grey pivot rather than 0.5: this runs before tone mapping,
      // on linear HDR values, where 0.5 is nowhere near mid-grey.
      color = (color - 0.18) * contrast + 0.18;

      // Split tone: warm the bright end, cool the dark end.
      color.r += warmth * luma;
      color.b += warmth * (1.0 - luma) * 0.8;

      // Vignette in aspect-corrected UV space so it stays circular on ultrawide displays.
      vec2 centered = vUv - 0.5;
      float falloff = smoothstep(0.78, 0.28, length(centered) * 1.42);
      color *= mix(1.0 - vignette, 1.0, falloff);

      gl_FragColor = vec4(max(color, 0.0), texel.a);
    }
  `,
};

export class PostFx {
  private composer: EffectComposer;
  private target: THREE.WebGLRenderTarget;
  private bloom: UnrealBloomPass;
  private grade: ShaderPass;
  private smaa: SMAAPass | null = null;
  readonly preset: Exclude<QualityPreset, 'low'>;

  constructor(
    private renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    preset: Exclude<QualityPreset, 'low'>,
  ) {
    this.preset = preset;
    const profile = PROFILES[preset];
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());

    // Our own HDR target so ACES has real headroom to bloom from, and so MSAA survives.
    this.target = new THREE.WebGLRenderTarget(size.x, size.y, {
      type: THREE.HalfFloatType,
      samples: profile.samples,
    });
    this.target.texture.name = 'PostFx.hdr';

    this.composer = new EffectComposer(renderer, this.target);
    // EffectComposer derives its pixel ratio from the renderer at construction; the renderer's
    // ratio is already set by applyPreferences before we get here.
    this.composer.addPass(new RenderPass(scene, camera));

    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(Math.max(1, size.x * profile.bloomScale), Math.max(1, size.y * profile.bloomScale)),
      profile.bloomStrength,
      profile.bloomRadius,
      profile.bloomThreshold,
    );
    this.composer.addPass(this.bloom);

    this.grade = new ShaderPass(GradeShader);
    this.grade.uniforms.saturation.value = profile.saturation;
    this.grade.uniforms.contrast.value = profile.contrast;
    this.grade.uniforms.vignette.value = profile.vignette;
    this.composer.addPass(this.grade);

    // Only worth running when the target has no MSAA of its own.
    if (profile.samples === 0) {
      this.smaa = new SMAAPass(size.x, size.y);
      this.composer.addPass(this.smaa);
    }

    // Must be last: applies ACES + sRGB + exposure, read live off the renderer each frame.
    this.composer.addPass(new OutputPass());
  }

  /** True when this instance still matches the requested preset; otherwise the renderer rebuilds. */
  matches(preset: QualityPreset): boolean {
    return preset !== 'low' && preset === this.preset;
  }

  setSize(width: number, height: number): void {
    // setPixelRatio calls setSize internally, so order matters.
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(width, height);
  }

  render(deltaSeconds: number): void {
    this.composer.render(deltaSeconds);
  }

  dispose(): void {
    this.bloom.dispose();
    this.grade.dispose();
    this.smaa?.dispose();
    this.composer.dispose();
    this.target.dispose();
  }
}
